/**
 * An agent's name where a stamp holds its principal (`agent:<team>/<member>`, `specialist:<id>`): board and Inbox records
 * written with roles on name the agent by its stable id. The organisation's agents are read once and shared; an actor
 * that is not an agent principal (older records name the agent in words) is shown as it is.
 */

import { useEffect, useRef, useState } from 'react';

import { rbacRoutes } from './api/client.ts';

type Names = Record<string, { name: string; team: string }>;

let cached: { at: number; names: Names } | undefined;
let inflight: Promise<Names> | undefined;
const FRESH_MS = 60_000;

function load(): Promise<Names> {
  if (cached && Date.now() - cached.at < FRESH_MS) return Promise.resolve(cached.names);
  inflight ??= rbacRoutes
    .agentNames()
    .then((r) => r.agents)
    .catch(() => cached?.names ?? {})
    .then((names) => {
      cached = { at: Date.now(), names };
      inflight = undefined;
      return names;
    });
  return inflight;
}

/** "Planner (Delivery team)" for a principal the organisation knows; the actor itself otherwise. */
export function agentLabel(names: Names, actor: string | undefined): string | undefined {
  if (!actor) return actor;
  const a = names[actor];
  if (!a) return actor;
  return a.team && a.team !== a.name ? `${a.name} (${a.team})` : a.name;
}

/** Whether the actor looks like a principal (so it is worth asking who it is). */
const isPrincipal = (actor: string | undefined): boolean => Boolean(actor && /^(agent|specialist):/.test(actor));

/** Resolves actors to agent names; reads the names once, and only when a principal is shown. */
export function useAgentName(): (actor: string | undefined) => string | undefined {
  const [names, setNames] = useState<Names>(cached?.names ?? {});
  const [wanted, setWanted] = useState(false);
  const seen = useRef(false);
  // After each render: a principal was shown, so ask who they are (once).
  useEffect(() => {
    if (seen.current && !wanted) setWanted(true);
  });
  useEffect(() => {
    if (!wanted) return;
    let live = true;
    void load().then((n) => live && setNames(n));
    return () => {
      live = false;
    };
  }, [wanted]);
  return (actor) => {
    if (isPrincipal(actor)) seen.current = true;
    return agentLabel(names, actor);
  };
}
